import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature dispatch-planner-events
import { getRouteClosOptions, getCurrentRouteCloEventGuids } from './helpers';
//////////////////////////////////////////////////

const selectBuilderStore = (state) => state.dispatchPlannerEventsV2;
const selectCurrentBranchConfigs = (state) => state.dispatchPlannerEventsV2.currentBranchConfigs;
const selectCloEventList = (state) => state.dispatchPlannerEventsV2.cloEventList;

const makeSelectBuilderStore = () => createSelector(
  selectBuilderStore,
  (state) => ({ ...state }),
);

const makeSelectCurrentRoute = () => (state) => selectBuilderStore(state).currentRoute;

const makeSelectOpenedFromPage = () => (state) => selectBuilderStore(state).openedFromPage;

const makeSelectBranchGuidForRequest = () => (state) => selectBuilderStore(state).branchGuidForRequest;

const makeSelectCurrentBranchConfigs = () => (state) => selectBuilderStore(state).currentBranchConfigs;

const makeSelectConcatReportFiltersConfig = () => createSelector(
  (state) => selectBuilderStore(state).currentBranchConfigs,
  (currentBranchConfigs) => G.getConfigValueFromStore(
    GC.UI_LOAD_PLANNER_CONCAT_REPORT_FILTERS,
    currentBranchConfigs,
  ),
);

const makeSelectCloEventReport = () => (state) => selectBuilderStore(state).cloEventReport;

const makeSelectTelReport = () => (state) => selectBuilderStore(state).telReport;

const makeSelectInboundTelReport = () => (state) => selectBuilderStore(state).inboundTelReport;

const makeSelectOutboundTelReport = () => (state) => selectBuilderStore(state).outboundTelReport;

const makeSelectCloEventList = () => (state) => selectBuilderStore(state).cloEventList;

const makeSelectTelList = () => (state) => selectBuilderStore(state).telList;

const makeSelectInboundTelList = () => (state) => selectBuilderStore(state).inboundTelList;

const makeSelectOutboundTelList = () => (state) => selectBuilderStore(state).outboundTelList;

const makeSelectCloEventListLoading = () => (state) => selectBuilderStore(state).cloEventListLoading;

const makeSelectTelListLoading = () => (state) => selectBuilderStore(state).telListLoading;

const makeSelectInboundTelListLoading = () => (state) => selectBuilderStore(state).inboundTelListLoading;

const makeSelectOutboundTelListLoading = () => (state) => selectBuilderStore(state).outboundTelListLoading;

const makeSelectCloEventPagination = () => (state) => selectBuilderStore(state).cloEventPagination;

const makeSelectTelPagination = () => (state) => selectBuilderStore(state).telPagination;

const makeSelectInboundTelPagination = () => (state) => selectBuilderStore(state).inboundTelPagination;

const makeSelectOutboundTelPagination = () => (state) => selectBuilderStore(state).outboundTelPagination;

const makeSelectCloEventTotal = () => (state) => selectBuilderStore(state).cloEventTotal;

const makeSelectTelTotal = () => (state) => selectBuilderStore(state).telTotal;

const makeSelectInboundTelTotal = () => (state) => selectBuilderStore(state).inboundTelTotal;

const makeSelectOutboundTelTotal = () => (state) => selectBuilderStore(state).outboundTelTotal;

const makeSelectFullScreenView = () => (state) => selectBuilderStore(state).fullScreenView;

const makeSelectAvailableCloEventReports = () => (state) => selectBuilderStore(state).availableCloEventReports;

const makeSelectAvailableTelReports = () => (state) => selectBuilderStore(state).availableTelReports;

const makeSelectRouteTelConfigs = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => G.getRouteTelConfigsForCreateClo(configs),
);

const makeSelectPrimaryReferenceAutogeneratedConfig = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => G.getConfigValueFromStore(
    GC.TEL_PRIMARY_REFERENCE_AUTOGENERATED,
    configs,
  ),
);

const makeSelectDefaultUomFields = () => createSelector(
  selectCurrentBranchConfigs,
  (configStore) => {
    let defaultValue = {};

    const uomSystem = G.getConfigValueFromStore(
      GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM,
      configStore,
    );

    if (R.and(G.isNotNil(uomSystem), G.isNotEmpty(uomSystem))) {
      defaultValue = GC.defaultSystemUoms[uomSystem];
    }

    return defaultValue;
  },
);

const makeSelectOptionsForSelect = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => {
    const serviceType = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      configs,
      GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
      true,
    );

    const transportationMode = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      configs,
      GC.GENERAL_MODE_TRANSPORTATION,
      true,
    );

    return {
      serviceType,
      transportationMode,
    };
  },
);

const makeSelectCloEventByGuid = (guid) => createSelector(
  selectCloEventList,
  (cloEventList = []) => R.find(R.propEq(guid, GC.FIELD_GUID), cloEventList),
);

const makeSelectTelByGuid = (guid) => createSelector(
  (state) => selectBuilderStore(state).telList,
  (state) => selectBuilderStore(state).inboundTelList,
  (state) => selectBuilderStore(state).outboundTelList,
  (telList, inboundTelList, outboundTelList) => {
    const tel = R.find(R.propEq(guid, GC.FIELD_GUID), R.or(telList, []));
    const inboundTel = R.find(R.propEq(guid, GC.FIELD_GUID), R.or(inboundTelList, []));
    const outboundTel = R.find(R.propEq(guid, GC.FIELD_GUID), R.or(outboundTelList, []));

    return tel || inboundTel || outboundTel;
  },
);

const makeSelectItemsForDrop = () => createSelector(
  (state) => selectBuilderStore(state).currentRouteItems,
  (currentRouteItems) => R.filter(
    (item) => R.and(
      R.propEq(false, 'selected', item),
      R.not(G.isLoadTypeClo(item)),
    ),
    R.values(currentRouteItems),
  ),
);

const makeSelectItemsForPickup = () => createSelector(
  (state) => selectBuilderStore(state).currentRouteItems,
  (currentRouteItems) => R.filter(
    (item) => R.and(
      R.propEq(true, 'selected', item),
      R.not(G.isLoadTypeClo(item)),
    ),
    R.values(currentRouteItems),
  ),
);

const makeSelectCurrentRouteItems = () => (state) => selectBuilderStore(state).currentRouteItems;

const makeSelectCurrentRouteContainers = () => (state) => selectBuilderStore(state).currentRouteContainers;

const makeSelectEventsInfo = () => createSelector(
  (state) => selectBuilderStore(state).currentRoute,
  (currentRoute) => {
    const tels = R.filter(
      (tel) => G.isNotNilAndNotEmpty(tel[GC.FIELD_LOAD_STOPS]),
      R.sortBy(
        R.prop('order'),
        R.values(currentRoute.tels),
      ),
    );

    if (G.isNilOrEmpty(tels)) {
      return {
        firstEvent: null,
        lastEvent: null,
        total: 0,
      };
    }

    return {
      firstEvent: R.head(R.sortBy(
        R.prop('telEventIndex'),
        R.values(R.head(tels).events),
      )),
      lastEvent: R.last(R.sortBy(
        R.prop('telEventIndex'),
        R.values(R.last(tels).events),
      )),
      total: R.reduce(
        (arr = [], tel) => R.concat(arr, R.values(tel[GC.FIELD_LOAD_STOPS])),
        [],
        tels,
      ).length,
    };
  },
);

const makeSelectRouteErrors = () => (state) => selectBuilderStore(state).routeErrors;

const locationFieldsToPick = [
  GC.FIELD_TEMPLATE_ID,
  GC.FIELD_LOCATION_NAME,
  GC.FIELD_CITY,
  GC.FIELD_STATE,
  GC.FIELD_ADDRESS_1,
  GC.FIELD_ZIP,
];

const makeSelectExistedTerminalOptions = () => createSelector(
  (state) => selectBuilderStore(state).currentRoute,
  (currentRoute) => R.compose(
    R.map((event) => ({
      label: G.concatLocationFields(R.prop('location', event), locationFieldsToPick),
      value: R.prop('stopId', event),
    })),
    R.uniqBy(R.prop('stopId')),
    R.reduce((acc, item) => R.concat(acc, item), []),
    R.map((tel) => R.compose(
      R.uniqBy(R.prop('stopId')),
      R.filter(R.propEq(GC.STOP_TYPE_TERMINAL, 'stopType')),
      R.values,
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tel)),
    R.values,
    R.prop('tels'),
  )(currentRoute),
);

const makeSelectSelectedTels = () => (state) => selectBuilderStore(state).selectedTels;

const makeSelectCrossDockLocationList = () => (state) => selectBuilderStore(state).crossDockLocationList;

const makeSelectInboundTerminals = () => (state) => selectBuilderStore(state).inboundTerminals;

const makeSelectOutboundTerminals = () => (state) => selectBuilderStore(state).outboundTerminals;

const makeSelectPlannerMode = () => (state) => selectBuilderStore(state).plannerMode;

const makeSelectHidePlanned = () => (state) => selectBuilderStore(state).hidePlanned;

const makeSelectGlobalFilterValue = () => (state) => selectBuilderStore(state).globalFilterValue;

const makeSelectGlobalFilterValueET = () => (state) => selectBuilderStore(state).globalFilterValueET;

const makeSelectGlobalFilterValueIT = () => (state) => selectBuilderStore(state).globalFilterValueIT;

const makeSelectGlobalFilterValueOT = () => (state) => selectBuilderStore(state).globalFilterValueOT;

const makeSelectUnassignedCloEvents = () => (state) => selectBuilderStore(state).unassignedCloEvents;

const makeSelectDeletedEventGuids = () => (state) => selectBuilderStore(state).deletedEventGuids;

const makeSelectInitPlannerData = () => (state) => selectBuilderStore(state).initPlannerData;

const makeSelectInitPlannerDataFromTemplate = () => (state) => selectBuilderStore(state).initPlannerDataFromTemplate;

const makeSelectEditedClos = () => (state) => selectBuilderStore(state).editedClos;

const makeSelectEditedCloGuids = () => createSelector(
  (state) => selectBuilderStore(state).editedClos,
  (editedClos) => R.keys(editedClos),
);

const makeSelectFilterProps = () => (state) => selectBuilderStore(state).filterProps;

const makeSelectCloEventsTableTitleSortValues = () => (state) => selectBuilderStore(state).cloEventsTitleSortValues;

const makeSelectTelTableTitleSortValues = () => (state) => selectBuilderStore(state).telTitleSortValues;

const makeSelectInboundTableTitleSortValues = () => (state) => selectBuilderStore(state).inboundTitleSortValues;

const makeSelectOutboundTableTitleSortValues = () => (state) => selectBuilderStore(state).outboundTitleSortValues;

const makeSelectCloEventsTableTitleFilters = () => (state) => selectBuilderStore(state).cloEventsTableTitleFilters;

const makeSelectTelTableTitleFilters = () => (state) => selectBuilderStore(state).telTableTitleFilters;

const makeSelectInboundTableTitleFilters = () => (state) => selectBuilderStore(state).inboundTableTitleFilters;

const makeSelectOutboundTableTitleFilters = () => (state) => selectBuilderStore(state).outboundTableTitleFilters;

const makeSelectCurrentRouteInitialState = () => (state) => selectBuilderStore(state).currentRouteInitialState;

const makeSelectTelsByCloGuidList = () => (state) => selectBuilderStore(state).telsByCloGuidList;

const makeSelectCurrentRouteCloOptions = () => createSelector(
  (state) => selectBuilderStore(state).currentRoute,
  (state) => selectBuilderStore(state).currentRouteItems,
  (currentRoute, currentRouteItems) => getRouteClosOptions(currentRoute, currentRouteItems),
);

const makeSelectCloRelatedTelListLoading = () => (state) => selectBuilderStore(state).cloRelatedTelListLoading;

const makeSelectRouteTemplates = () => (state) => selectBuilderStore(state).routeTemplates;

const makeSelectTelCreatedByFilter = () => (state) => selectBuilderStore(state).telCreatedByFilter;

const makeSelectCloEventCreatedByFilter = () => (state) => selectBuilderStore(state).cloEventCreatedByFilter;

const makeSelectInboundTelCreatedByFilter = () => (state) => selectBuilderStore(state).inboundTelCreatedByFilter;

const makeSelectOutboundTelCreatedByFilter = () => (state) => selectBuilderStore(state).outboundTelCreatedByFilter;

// clo event list for map
const makeSelectCloEventListForMap = () => createSelector(
  (state) => selectBuilderStore(state).currentRoute,
  (state) => selectBuilderStore(state).cloEventListForMap,
  (currentRoute, cloEventListForMap) => {
    const selectedGuids = new Set(getCurrentRouteCloEventGuids(currentRoute));

    return R.map((stop) => {
      const lat = G.getPropFromObject(GC.GRC.LOCATION_LATITUDE, stop);
      const lng = G.getPropFromObject(GC.GRC.LOCATION_LONGITUDE, stop);
      const selected = selectedGuids.has(G.getGuidFromObject(stop));
      const latLng = { lat: parseFloat(lat), lng: parseFloat(lng) };

      return R.mergeRight(stop, { latLng, selected });
    }, cloEventListForMap);
  },
);

const makeSelectCloEventListForMapLoading = () => (state) => selectBuilderStore(state).cloEventListForMapLoading;

const makeSelectCloEventListForMapTotal = () => createSelector(
  (state) => selectBuilderStore(state).cloEventListForMap,
  (cloEventListForMap) => R.length(cloEventListForMap),
);

const makeSelectStopsInfoList = () => (state) => selectBuilderStore(state).stopsInfoList;

export {
  makeSelectTelList,
  makeSelectTelTotal,
  makeSelectTelReport,
  makeSelectTelByGuid,
  makeSelectEventsInfo,
  makeSelectEditedClos,
  makeSelectFilterProps,
  makeSelectRouteErrors,
  makeSelectPlannerMode,
  makeSelectHidePlanned,
  makeSelectBuilderStore,
  makeSelectCurrentRoute,
  makeSelectItemsForDrop,
  makeSelectSelectedTels,
  makeSelectCloEventList,
  makeSelectCloEventTotal,
  makeSelectTelPagination,
  makeSelectStopsInfoList,
  makeSelectOpenedFromPage,
  makeSelectCloEventReport,
  makeSelectCloEventByGuid,
  makeSelectItemsForPickup,
  makeSelectTelListLoading,
  makeSelectFullScreenView,
  makeSelectInboundTelList,
  makeSelectEditedCloGuids,
  makeSelectRouteTemplates,
  makeSelectRouteTelConfigs,
  makeSelectOutboundTelList,
  makeSelectInboundTelTotal,
  makeSelectInitPlannerData,
  makeSelectDefaultUomFields,
  makeSelectOptionsForSelect,
  makeSelectInboundTelReport,
  makeSelectOutboundTelTotal,
  makeSelectInboundTerminals,
  makeSelectCurrentRouteItems,
  makeSelectOutboundTelReport,
  makeSelectOutboundTerminals,
  makeSelectGlobalFilterValue,
  makeSelectDeletedEventGuids,
  makeSelectTelsByCloGuidList,
  makeSelectCloEventPagination,
  makeSelectTelCreatedByFilter,
  makeSelectAvailableTelReports,
  makeSelectCloEventListLoading,
  makeSelectUnassignedCloEvents,
  makeSelectGlobalFilterValueET,
  makeSelectGlobalFilterValueIT,
  makeSelectGlobalFilterValueOT,
  makeSelectBranchGuidForRequest,
  makeSelectCurrentBranchConfigs,
  makeSelectInboundTelPagination,
  makeSelectTelTableTitleFilters,
  makeSelectInboundTelListLoading,
  makeSelectCrossDockLocationList,
  makeSelectOutboundTelPagination,
  makeSelectExistedTerminalOptions,
  makeSelectOutboundTelListLoading,
  makeSelectCurrentRouteCloOptions,
  makeSelectCurrentRouteContainers,
  makeSelectTelTableTitleSortValues,
  makeSelectCloEventCreatedByFilter,
  makeSelectAvailableCloEventReports,
  makeSelectInboundTableTitleFilters,
  makeSelectCurrentRouteInitialState,
  makeSelectConcatReportFiltersConfig,
  makeSelectCloRelatedTelListLoading,
  makeSelectOutboundTableTitleFilters,
  makeSelectInboundTelCreatedByFilter,
  makeSelectOutboundTelCreatedByFilter,
  makeSelectCloEventsTableTitleFilters,
  makeSelectInitPlannerDataFromTemplate,
  makeSelectInboundTableTitleSortValues,
  makeSelectOutboundTableTitleSortValues,
  makeSelectCloEventsTableTitleSortValues,
  makeSelectPrimaryReferenceAutogeneratedConfig,
  // clo event list for map
  makeSelectCloEventListForMap,
  makeSelectCloEventListForMapTotal,
  makeSelectCloEventListForMapLoading,
};
